'use client';

import React, { useEffect, useState } from 'react';
import { getStudyLog } from '@/lib/storage';
import { getWeekData, DAY_SHORT, PHASE_META } from '@/lib/curriculum';
import { Activity } from 'lucide-react';

interface ActivityItem {
  id: string;
  weekNum: number;
  dayIndex: number;
  date: string;
  topic: string;
  phase: number;
}

export default function RecentActivity() {
  const [items, setItems] = useState<ActivityItem[]>([]);

  useEffect(() => {
    const log = getStudyLog();
    const recent = [...log]
      .sort((a, b) => b.date.localeCompare(a.date))
      .slice(0, 6);

    const built: ActivityItem[] = recent.map((entry, idx) => {
      const week = getWeekData(entry.weekNum);
      return {
        id: `activity-${entry.weekNum}-${entry.dayIndex}-${idx}`,
        weekNum: entry.weekNum,
        dayIndex: entry.dayIndex,
        date: entry.date,
        topic: week?.days[entry.dayIndex]?.topic ?? 'Unknown topic',
        phase: week?.phase ?? 1,
      };
    });

    // If no log entries, show a helpful empty state
    setItems(built);
  }, []);

  if (items.length === 0) {
    return (
      <div
        style={{
          backgroundColor: 'var(--bg-card)',
          border: '1px solid var(--border)',
          borderRadius: '12px',
          padding: '20px',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '10px',
          minHeight: '180px',
        }}
      >
        <Activity size={28} style={{ color: 'var(--text-muted)' }} />
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--text-secondary)', marginBottom: '4px' }}>
            No activity yet
          </div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)' }}>
            Complete your first task to see it here
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      style={{
        backgroundColor: 'var(--bg-card)',
        border: '1px solid var(--border)',
        borderRadius: '12px',
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
      }}
    >
      <h2 style={{ fontSize: '14px', fontWeight: 600, color: 'var(--text-primary)', letterSpacing: '0.01em' }}>
        Recent Activity
      </h2>

      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
        {items.map(item => {
          const phase = PHASE_META[item.phase];
          const dateLabel = item.date === new Date().toISOString().split('T')[0]
            ? 'Today'
            : item.date === new Date(Date.now() - 86400000).toISOString().split('T')[0]
              ? 'Yesterday'
              : item.date;

          return (
            <div
              key={item.id}
              style={{
                display: 'flex',
                alignItems: 'flex-start',
                gap: '10px',
                padding: '8px 10px',
                borderRadius: '8px',
                backgroundColor: 'var(--bg-elevated)',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <div
                style={{
                  width: '6px',
                  height: '6px',
                  borderRadius: '50%',
                  backgroundColor: phase.textColor,
                  marginTop: '5px',
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: '13px',
                    color: 'var(--text-primary)',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                  }}
                >
                  {item.topic}
                </div>
                <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginTop: '2px', fontFamily: 'IBM Plex Mono, monospace' }}>
                  Wk {item.weekNum} · {DAY_SHORT[item.dayIndex]} · {dateLabel}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}